import React, { memo, useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { useFileDownload, useGetFiles } from 'src/hooks/downloadMultipleFiles';
import { useSearchParams } from 'react-router-dom';
import { useTranslate } from '@im/react-localization';

type DownloadMultipleFilesProps = Readonly<{
  headerId: number;
  label: string;
}>;

export function DownloadMultipleFiles(props: DownloadMultipleFilesProps) {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { onDownloadClick } = useFileDownload();

  const getFiles = useGetFiles();

  const files = getFiles.data?.files ?? [];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    getFiles
      .mutateAsync({
        countryCode: countryCode ?? '',
        headerId: props.headerId,
        multiFile: true,
      })
      .then((res) => {
        if (res.files.length === 1) {
          handleClose();
          onDownloadClick({
            countryCode: countryCode ?? '',
            fileName: res.files[0].filePath,
          });
        }
      });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDownload = (filePath: string) => {
    onDownloadClick({
      countryCode: countryCode ?? '',
      fileName: filePath,
    });
  };

  return (
    <>
      <Button
        data-testid="gds-download-multiple-files-button"
        onClick={handleClick}
        startIcon={<FileDownloadIcon />}
        variant="newOutlined"
      >
        {props.label}
      </Button>
      <Menu
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: anchorEl?.clientWidth ?? 180,
          },
        }}
        anchorEl={anchorEl}
        id="download-menu"
        onClose={handleClose}
        open={open}
      >
        {getFiles.isLoading && (
          <MenuItem dense disabled>
            {translate('Common.Loading')}
          </MenuItem>
        )}
        {files.length > 1 &&
          files.map((file) => (
            <MenuItem
              dense
              key={file.filePath}
              onClick={() => {
                onDownload(file.filePath);
                handleClose();
              }}
            >
              {file.filePath.split('/').pop()}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}

export default memo(DownloadMultipleFiles);
